import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { NbInput, NbButton } from "../components/nb";
import { Send, Check, CheckCheck, ImagePlus, MessageCircle } from "lucide-react";

function fileToBase64(file) {
  return new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });
}

export default function Messages() {
  const { otherId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [conv, setConv] = useState(null);
  const [text, setText] = useState("");
  const bottomRef = useRef(null);

  const loadThreads = async () => {
    try { const { data } = await api.get("/messages/threads"); setThreads(data); } catch {}
  };
  const loadConv = async (id) => {
    try { const { data } = await api.get(`/messages/${id}`); setConv(data); loadThreads(); } catch {}
  };

  useEffect(() => { loadThreads(); }, []);
  useEffect(() => { if (otherId) loadConv(otherId); }, [otherId]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [conv]);

  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim() || !otherId) return;
    const t = text; setText("");
    try { await api.post("/messages", { to_user_id: otherId, text: t }); loadConv(otherId); } catch {}
  };

  const sendImage = async (e) => {
    const file = e.target.files[0];
    if (!file || !otherId) return;
    const b64 = await fileToBase64(file);
    try { await api.post("/messages", { to_user_id: otherId, image: b64 }); loadConv(otherId); } catch {}
  };

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-head font-black text-4xl tracking-tighter mb-6">Messages</h1>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[70vh]">
          {/* Threads list */}
          <div className="md:col-span-1 bg-white nb-border nb-shadow overflow-y-auto">
            {threads.length === 0 ? (
              <div className="p-6 text-center text-gray-500 font-medium">No conversations yet.</div>
            ) : threads.map((t) => (
              <button key={t.other_id} onClick={() => navigate(`/messages/${t.other_id}`)} data-testid={`thread-${t.other_id}`}
                className={`w-full text-left p-4 nb-border border-x-0 border-t-0 flex items-center gap-3 ${otherId === t.other_id ? "bg-[#F3F3F1]" : "bg-white"}`}>
                <div className="w-12 h-12 nb-border bg-[#F3F3F1] overflow-hidden flex-shrink-0">
                  <img src={t.avatar || `https://api.dicebear.com/7.x/thumbs/svg?seed=${t.other_id}`} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-head font-bold truncate">{t.name}</div>
                  <div className="text-sm text-gray-500 truncate">{t.last_text}</div>
                </div>
                {t.unread > 0 && <span className="bg-[#8B1E3F] text-white text-xs font-bold px-2 py-0.5 nb-border">{t.unread}</span>}
              </button>
            ))}
          </div>

          {/* Conversation */}
          <div className="md:col-span-2 bg-white nb-border nb-shadow flex flex-col">
            {!conv ? (
              <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
                <MessageCircle className="w-12 h-12 mb-2" /><p className="font-medium">Select a conversation</p>
              </div>
            ) : (
              <>
                <div className="p-4 nb-border border-x-0 border-t-0 font-head font-black text-lg flex items-center gap-2">
                  <div className="w-9 h-9 nb-border overflow-hidden"><img src={conv.other.avatar || `https://api.dicebear.com/7.x/thumbs/svg?seed=${conv.other.id}`} alt="" className="w-full h-full object-cover" /></div>
                  {conv.other.name}
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#F3F3F1]">
                  {conv.messages.map((m) => {
                    const mine = m.from_user_id === user.id;
                    return (
                      <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[75%] nb-border p-2 ${mine ? "bg-[#8B1E3F] text-white" : "bg-white"}`}>
                          {m.image && <img src={m.image} alt="" className="max-w-[200px] nb-border mb-1" />}
                          {m.text && <p className="font-medium text-sm break-words">{m.text}</p>}
                          <div className={`flex items-center gap-1 justify-end text-[10px] mt-1 ${mine ? "text-white/70" : "text-gray-400"}`}>
                            {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                            {mine && (m.read ? <CheckCheck className="w-3 h-3" /> : <Check className="w-3 h-3" />)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={bottomRef} />
                </div>
                <form onSubmit={send} className="p-3 nb-border border-x-0 border-b-0 flex gap-2">
                  <label className="bg-[#D4AF37] nb-border nb-shadow-sm nb-hover p-3 cursor-pointer flex items-center" data-testid="msg-image-label">
                    <ImagePlus className="w-5 h-5" />
                    <input type="file" accept="image/*" className="hidden" onChange={sendImage} data-testid="msg-image" />
                  </label>
                  <NbInput data-testid="msg-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message..." />
                  <NbButton type="submit" data-testid="msg-send"><Send className="w-4 h-4" /></NbButton>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
